import { inject, Injectable } from '@angular/core';
import { AccessType, CollaborationsDataService, Employee, EmployeeStatus, Patient } from '@ffd/api-client';
import { DropdownItem } from '@libs/ui-lib';

import { DataState, DISABLE_GLOBAL_ERROR_TOASTS, FfdComponentStore, isNotNull, Loadable } from '@libs/utils';
import { tapResponse, concatLatestFrom } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, filter, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectCurrentUserProfile, selectUserSites } from '@libs/store';
import { HttpContext } from '@angular/common/http';
import { ToastService } from '@libs/shared-services';
import { Participant, ParticipantListItem, ParticipantListItemType, ParticipantsList } from '../search-participants';
import { SelectedParticipant } from '../selected-participant';

const OPTIONS_LIMIT = 20;
export const MINIMUM_SEARCH_LENGTH = 1;

interface CollaborationEditorState {
  siteId: string;
  employees: Loadable<Employee[]>;
  employeesFromSearch: boolean;
  totalEmployeesCount: number;
  lastEmployeeSearchTerm: string | null;
  patients: Loadable<Patient[]>;
  totalPatientsCount: number;
  lastPatientSearchTerm: string;
  selectedEmployeeParticipants: SelectedParticipant[];
  emailParticipants: SelectedParticipant[] | null;
  selectedPatientParticipant: SelectedParticipant | null;
  linkedPatient: SelectedParticipant | null;
  preSelectedParticipantIds: string[];
  isPatientOnSite: boolean;
  isExternalOnSite: boolean;
}

export interface CollaborationEditorVM {
  employeeOptions?: ParticipantsList;
  patientOptions?: ParticipantsList;
  employeesLoading: boolean;
  patientsLoading: boolean;
  siteId?: string;
  employeeParticipants: SelectedParticipant[];
  emailParticipants: SelectedParticipant[] | null;
  patientParticipant: SelectedParticipant | null;
  allParticipants: SelectedParticipant[];
  linkedPatient: SelectedParticipant | null;
  isPatientOnSite: boolean;
  isExternalOnSite: boolean;
  showcasePatientDropdownOptions: DropdownItem<string>[];
}

@Injectable()
export class CollaborationEditorDialogService extends FfdComponentStore<CollaborationEditorState, CollaborationEditorVM> {
  private readonly collaborationsDataService = inject(CollaborationsDataService);
  private readonly translateService = inject(TranslateService);
  private readonly toastMessageService = inject(ToastService);
  private readonly store = inject(Store);

  public currentUser = this.store.selectSignal(selectCurrentUserProfile)();

  constructor() {
    super({
      siteId: '',
      selectedEmployeeParticipants: [],
      selectedPatientParticipant: null,
      linkedPatient: null,
      employees: {
        data: undefined,
        state: DataState.Initial,
      },
      employeesFromSearch: false,
      emailParticipants: [],
      totalEmployeesCount: 0,
      lastEmployeeSearchTerm: '',
      patients: {
        data: undefined,
        state: DataState.Initial,
      },
      totalPatientsCount: 0,
      lastPatientSearchTerm: '',
      preSelectedParticipantIds: [],
      isPatientOnSite: false,
      isExternalOnSite: false,
    });
  }

  public patients$ = this.select((state) => state.patients);
  public patientsTotalCount$ = this.select((state) => state.totalPatientsCount);

  public setLinkedPatient = this.updater((state, linkedPatient: SelectedParticipant): CollaborationEditorState => {
    return {
      ...state,
      linkedPatient,
    };
  });

  public setEmployeesLoading = this.updater(
    (state): CollaborationEditorState => ({
      ...state,
      employees: {
        ...state.employees,
        state: DataState.Loading,
      },
    }),
  );

  public setPatientsLoading = this.updater(
    (state): CollaborationEditorState => ({
      ...state,
      patients: {
        ...state.patients,
        state: DataState.Loading,
      },
    }),
  );

  protected setLastProviderSearchTerm = this.updater((state, lastSearchTerm: string | null): CollaborationEditorState => {
    return {
      ...state,
      lastEmployeeSearchTerm: lastSearchTerm,
    };
  });

  protected setEmployees = this.updater((state, employee: Employee[]): CollaborationEditorState => {
    return {
      ...state,
      employees: {
        data: employee,
        state: DataState.Fetched,
      },
    };
  });

  protected appendEmployees = this.updater((state, employee: Employee[]): CollaborationEditorState => {
    return {
      ...state,
      employees: {
        data: state.employees.data?.concat(employee) ?? employee,
        state: DataState.Fetched,
      },
    };
  });

  public setPatients = this.updater((state, patients: Patient[]): CollaborationEditorState => {
    return {
      ...state,
      patients: {
        data: patients,
        state: DataState.Fetched,
      },
    };
  });

  public appendPatients = this.updater((state, patients: Patient[]): CollaborationEditorState => {
    return {
      ...state,
      patients: {
        data: state.patients.data?.concat(patients) ?? patients,
        state: DataState.Fetched,
      },
    };
  });

  protected setTotalProvidersCount = this.updater((state, totalCount: number): CollaborationEditorState => {
    return {
      ...state,
      totalEmployeesCount: totalCount,
    };
  });

  public setTotalPatientsCount = this.updater((state, totalCount: number): CollaborationEditorState => {
    return {
      ...state,
      totalPatientsCount: totalCount,
    };
  });

  public setParticipantsFromPersons = this.updater((state, participants: SelectedParticipant[]): CollaborationEditorState => {
    return {
      ...state,
      selectedEmployeeParticipants: participants,
    };
  });

  public readonly appendEmployeeParticipant = this.updater((state, participant: SelectedParticipant): CollaborationEditorState => {
    return {
      ...state,
      selectedEmployeeParticipants: [...state.selectedEmployeeParticipants, participant],
    };
  });

  public readonly appendEmailParticipant = this.updater((state, participant: SelectedParticipant): CollaborationEditorState => {
    return {
      ...state,
      emailParticipants: [...(state.emailParticipants ?? []), participant],
    };
  });

  public readonly setPatientParticipant = this.updater((state, participant: SelectedParticipant): CollaborationEditorState => {
    return {
      ...state,
      selectedPatientParticipant: participant,
      linkedPatient: participant,
    };
  });

  public readonly setEmailParticipant = this.updater((state, participants: SelectedParticipant[]): CollaborationEditorState => {
    return {
      ...state,
      emailParticipants: participants,
    };
  });

  public readonly removeEmployeeParticipant = this.updater((state, participant: SelectedParticipant): CollaborationEditorState => {
    return {
      ...state,
      selectedEmployeeParticipants: state.selectedEmployeeParticipants.filter(
        (selectedParticipant) => selectedParticipant.id !== participant.id,
      ),
    };
  });

  public readonly removeEmailParticipant = this.updater((state, participant: SelectedParticipant): CollaborationEditorState => {
    return {
      ...state,
      emailParticipants: state.emailParticipants?.filter((selectedParticipant) => selectedParticipant.id !== participant.id) ?? [],
    };
  });

  public override init(): void {}

  protected override mapStateToViewModel(state: CollaborationEditorState): CollaborationEditorVM {
    return {
      siteId: state.siteId,
      employeeOptions: this.mapProvidersToParticipantList(state.employees.data, state.selectedEmployeeParticipants),
      patientOptions: this.mapPatientsToParticipantList(state.patients.data, state.selectedPatientParticipant),
      employeesLoading: state.employees.state === DataState.Loading,
      patientsLoading: state.patients.state === DataState.Loading,
      employeeParticipants: state.selectedEmployeeParticipants,
      emailParticipants: state.emailParticipants,
      patientParticipant: state.selectedPatientParticipant,
      linkedPatient: state.linkedPatient,
      isPatientOnSite: state.isPatientOnSite,
      isExternalOnSite: state.isExternalOnSite,
      allParticipants: [
        ...state.selectedEmployeeParticipants,
        ...(state.selectedPatientParticipant ? [state.selectedPatientParticipant] : []),
        ...(state.emailParticipants ?? []),
      ],
      showcasePatientDropdownOptions:
        state.patients.data?.map((patient) => ({
          label: `${patient.firstName} ${patient.lastName}`,
          value: patient.id,
        })) ?? [],
    };
  }

  public setSiteId(siteId: string): void {
    const sites = this.store.selectSignal(selectUserSites)();
    const selectedSite = sites.find((site) => site.id === siteId);
    const isPatientOnSite = isNotNull(selectedSite?.patient) && !selectedSite?.provider;
    const isExternalOnSite =
      (isNotNull(selectedSite?.provider) &&
        !selectedSite?.patient &&
        selectedSite?.provider.accessType === AccessType.COLLAB_ONLY_ACCESS) ||
      selectedSite?.staff?.accessType === AccessType.COLLAB_ONLY_ACCESS;

    this.patchState({
      siteId,
      isPatientOnSite,
      isExternalOnSite,
      linkedPatient: isPatientOnSite
        ? {
            id: selectedSite!.patient!.id,
            firstName: selectedSite!.patient!.firstName,
            lastName: selectedSite!.patient!.lastName,
            participantRole: 'patient',
          }
        : null,
      patients: { data: [], state: DataState.Initial },
      employees: { data: [], state: DataState.Initial },
      selectedEmployeeParticipants: [],
      selectedPatientParticipant: null,
    });
  }

  public updateProviderSearchTerm(searchTerm: string | null): void {
    if (!searchTerm || (searchTerm && searchTerm !== this.state().lastEmployeeSearchTerm)) {
      this.patchState({
        employees: { data: [], state: DataState.Loading },
      });
    }

    if (!searchTerm) {
      this.searchProviders({ searchTerm: undefined });
    }

    this.setLastProviderSearchTerm(searchTerm);
    if (searchTerm && searchTerm.length >= MINIMUM_SEARCH_LENGTH) {
      this.searchProviders({ searchTerm });
    }
  }

  public readonly searchProviders = this.effect<{ searchTerm?: string; endReached?: boolean }>(($) =>
    $.pipe(
      concatLatestFrom(() => [
        this.select((state) => state.employees),
        this.select((state) => state.totalEmployeesCount),
        this.select((state) => state.siteId),
        this.select((state) => state.lastEmployeeSearchTerm),
      ]),
      distinctUntilChanged((a, b) => {
        return (
          a[0].searchTerm === b[0].searchTerm &&
          a[1].data?.length === b[1].data?.length &&
          (a[0].endReached || b[0].endReached || a[1].state === b[1].state)
        );
      }),
      filter(([_, providers, totalCount]) => {
        return !totalCount || totalCount > (providers.data?.length ?? 0);
      }),
      switchMap(([{ searchTerm, endReached }, providers, __, siteId, lastSearchTerm]) => {
        this.setEmployeesLoading();
        const offset = providers.data?.length ?? 0;
        const nextSearchTerm = endReached ? lastSearchTerm : searchTerm;
        return this.collaborationsDataService
          .searchParticipantsEmployees(OPTIONS_LIMIT, offset, siteId, nextSearchTerm ?? '', undefined, undefined, undefined, {
            context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
          })
          .pipe(
            tapResponse(
              ({ list, total }) => {
                if (searchTerm) {
                  this.setEmployees(list);
                } else {
                  this.appendEmployees(list);
                }
                this.setTotalProvidersCount(total);
              },
              () => {
                this.toastMessageService.error(
                  this.translateService.instant('collab_new_collab_modal_collaboration_toast_search_providers_error'),
                );
              },
            ),
          );
      }),
    ),
  );

  private readonly mapProvidersToParticipantList = (
    employees?: Employee[],
    excludedParticipants?: SelectedParticipant[],
  ): ParticipantsList | undefined => {
    if (!employees) {
      return;
    }

    const filteredProviders = employees?.filter((participant) => {
      return !excludedParticipants?.some((excludedParticipant) => {
        return excludedParticipant.id === participant.id;
      });
    });

    return filteredProviders
      .reduce(
        (acc, employee) => {
          const providerParticipant: Participant = {
            ...employee,
            type: ParticipantListItemType.Participant,
          };
          // Find the index of the group with the same status
          const groupIndex = acc.findIndex((item) => item.status === employee.status);

          const title =
            employee.status === EmployeeStatus.NOT_INVITED
              ? this.translateService.instant('collab_new_collab_modal_collaboration_not_invited_group')
              : employee.status === EmployeeStatus.PENDING
                ? this.translateService.instant('collab_new_collab_modal_collaboration_pending_group')
                : this.translateService.instant('collab_new_collab_modal_collaboration_providers_group');
          // If the group does not exist, create a new group
          if (groupIndex === -1) {
            acc.push({
              title,
              status: employee.status,
              listItem: [providerParticipant],
            });
          } else {
            // If the group exists, push the provider into the group
            acc[groupIndex].listItem.push(providerParticipant);
          }

          return acc;
        },
        [] as Array<{ title: string; status?: EmployeeStatus; listItem: ParticipantListItem[] }>,
      )
      .reduce((acc, group) => {
        acc.push({ title: group.title, type: ParticipantListItemType.GroupDivider }, ...group.listItem);
        return acc;
      }, [] as ParticipantListItem[]);
  };

  protected readonly mapPatientsToParticipantList = (
    patients?: Patient[],
    excludedParticipant?: SelectedParticipant | null,
  ): ParticipantsList | undefined => {
    if (!patients) {
      return;
    }

    const filteredPatients = patients?.filter((participant) => {
      return excludedParticipant?.id !== participant.id;
    });

    return filteredPatients.map((patient) => ({
      ...patient,
      type: ParticipantListItemType.Participant,
    }));
  };
}
